import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getValidatorFn } from 'src/Shared/filterValidators/validatorModel';
import { RootState } from '../../app/store';
import { Filter, FilterName, getFilterModelByName, getFilterNameString, IFilter } from './filterModels';

export interface FiltersState {
  value: IUpdatableFilterFields[];
  displayedFilters: FilterName[];
}

export type IUpdatableFilterFields = Partial<Omit<IFilter, 'name'>> & {
  name: FilterName;
};
// export type IUpdatableFilterFields = Partial<IFilter>;

const initialState: FiltersState = {
  value: [],
  displayedFilters: [],
};
export const slice = createSlice({
  name: 'filters',
  initialState,
  reducers: {
    initializeDefaultFiltersAction: (state: FiltersState, action: PayloadAction<Filter[]>) => {
      state.value = action.payload.map<IUpdatableFilterFields>((filter: Filter) => {
        const { name, displayName, validators, transformFunctionIndex, isValid, hidden, disabled } =
          getFilterModelByName(filter);
        return {
          name,
          displayName,
          validators,
          transformFunctionIndex,
          isValid,
          hidden,
          disabled,
          data: null,
        };
      });
      state.displayedFilters = action.payload.map((filter) => getFilterNameString(filter));
    },
    upsertFilterAction: (state: FiltersState, action: PayloadAction<IUpdatableFilterFields>) => {
      let filter = state.value.find((i) => i.name === action.payload.name);
      if (filter === undefined || filter === null) {
        state.value.push(action.payload);
        return;
      }
      const filterModel = getFilterModelByName(filter.name);

      Object.assign(filter, action.payload);
      filter['data'] = action.payload.data;
      // if (filter && !(action.payload.data === null || action.payload.data === undefined)) {
      //   filter['data'] = action.payload.data;
      //   // valid status needs to be update as well.
      // }

      if (filterModel?.validators !== null && filterModel?.validators !== undefined) {
        filter!['isValid'] = filterModel?.validators.every((validator) => {
          const validationResult =
            typeof validator === 'string'
              ? getValidatorFn(validator)(action.payload.data, state.value)
              : validator(action.payload.data, state.value);
          if (!validationResult.isValid) {
            console.log('validationResult.error', validationResult.error, action.payload.data, filter.displayName);
            filter!['error'] = validationResult.error;
          }
          // console.log('validationResult', validationResult, action.payload);
          return validationResult.isValid;
        });
        filter!['error'] = filter!['isValid'] ? undefined : filter?.error;
      }
    },
    updateFilterAction: (state: FiltersState, action: PayloadAction<IUpdatableFilterFields>) => {
      let index = state.value.findIndex((i) => i.name === action.payload.name);
      if (index === -1) {
        return;
      } else {
        Object.assign(state.value[index], action.payload);
      }
    },
    addFilterAction: (state: FiltersState, action: PayloadAction<IFilter>) => {
      state.value.push(action.payload);
    },
    removeFilterByNameAction: (state: FiltersState, action: PayloadAction<FilterName>) => {
      state.value.splice(
        state.value.findIndex((i) => i.name === action.payload),
        1,
      );
    },
    validateAction: (state: FiltersState) => {
      state.value.forEach((filter) => {
        const filterModel = getFilterModelByName(filter.name);
        if (filter.data !== null && filterModel?.validators !== null && filterModel?.validators !== undefined) {
          filter!['isValid'] = filterModel?.validators.every((validator) => {
            const validationResult =
              typeof validator === 'string'
                ? getValidatorFn(validator)(filter.data, state.value)
                : validator(filter.data, state.value);
            if (!validationResult.isValid) {
              console.log('validationResult.error', validationResult.error, filter.data, filter.displayName);
              filter!['error'] = validationResult.error;
            }
            return validationResult.isValid;
          });
          filter!['error'] = filter!['isValid'] ? undefined : filter?.error;
        }
      });
    },
  },
});
export const {
  initializeDefaultFiltersAction,
  addFilterAction,
  updateFilterAction,
  removeFilterByNameAction,
  upsertFilterAction,
  validateAction,
} = slice.actions;
export const selectAllFilters = (state: RootState) => state.filters.value;
// after changing this method, everytime the selector return a different object even if the value remains the same.
// If any useEffect depends on the returned value, make sure not to compare the object itself as it'll always change,
// This can lead to maximum stack error
// export const getFilterValueByName = (filterName: Filter) =>
//   createSelector(selectAllFilters, (filters) => {
//     let filterNameString = getFilterNameString(filterName);
//     const filter: IUpdatableFilterFields | undefined = filters.find((i) => i.name === filterNameString);
//     return filter;
//   });

/*
only return filter value from this selector, previous selector always return a new object no matter what, which breaks the
cache
* */
export const getFilterValueByName = (filterName: Filter) => (state: RootState) => {
  let filterNameString = getFilterNameString(filterName);
  return state.filters.value.find((i) => i.name === filterNameString);
};
export const getFiltersByNames = (filterNames: Filter[]) => (state: RootState) => {
  return state.filters.value.filter((i) => filterNames.findIndex((j) => j === i.name) !== -1);
};
export const getFilterNames = (state: RootState) => {
  return state.filters.displayedFilters;
};
// for fetching report data
export const getFilterDataRequestMapping = (state: RootState) => {
  let requestBody: { [index: string]: any } = {};
  state.filters.value.forEach((i) => {
    const filterModel = getFilterModelByName(i.name);

    if (filterModel.filterNamesInDataRequestBody && !(i.data === null || i.data === undefined)) {
      let transformFunction: (arg: any, ...extra: any) => any;
      if (Array.isArray(filterModel.transformDataToDataRequestFunctions)) {
        transformFunction = filterModel.transformDataToDataRequestFunctions[filterModel?.transformFunctionIndex || 0];
      } else if (
        filterModel.transformDataToDataRequestFunctions === null ||
        filterModel.transformDataToDataRequestFunctions === undefined
      ) {
        throw new Error(`transform function doesn't exist for this filter -> ${filterModel.displayName}`);
      } else {
        transformFunction = filterModel.transformDataToDataRequestFunctions!;
      }

      if (Array.isArray(filterModel.filterNamesInDataRequestBody)) {
        filterModel.filterNamesInDataRequestBody.forEach((filterName) => {
          requestBody[filterName] = transformFunction!(i.data, {
            filterName,
          });
        });
      } else {
        requestBody[filterModel.filterNamesInDataRequestBody] = transformFunction!(i.data);
      }
    }
  });
  return requestBody;
};
// for pdf/excel downloading api request body handling
export const getFilterDataFileRequestMapping = (state: RootState) => {
  let requestBody: { [index: string]: any } = {};
  state.filters.value.forEach((i) => {
    const filterModel = getFilterModelByName(i.name);
    const filterNames = filterModel.filterNamesInFileRequestBody ?? filterModel.filterNamesInDataRequestBody;
    if (filterNames && !(i.data === null || i.data === undefined)) {
      const transforms =
        filterModel.transformDataToFileRequestFunctions || filterModel.transformDataToDataRequestFunctions;
      let transformFunction: (arg: any, ...extra: any) => any;
      if (Array.isArray(transforms)) {
        transformFunction = transforms[filterModel?.transformFunctionIndex || 0];
      } else if (transforms === null || transforms === undefined) {
        throw new Error(`transform function doesn't exist for this filter -> ${filterModel.displayName}`);
      } else {
        transformFunction = transforms!;
      }

      if (Array.isArray(filterNames)) {
        filterNames.forEach((filterName) => {
          requestBody[filterName] = transformFunction!(i.data, {
            filterName,
          });
        });
      } else {
        requestBody[filterNames] = transformFunction!(i.data);
      }
    }
  });
  return requestBody;
};
export const getFilterDataValidateStatus = (state: RootState) => {
  let statusMapping: { [index: string]: true | string | undefined } = {};
  state.filters.value.forEach((i) => {
    const filterModel = getFilterModelByName(i.name);
    // only filters that need to send data to API have validate status
    if (filterModel.filterNamesInDataRequestBody || filterModel.filterNamesInFileRequestBody) {
      statusMapping[i.name] = i.isValid === true ? true : i.error;
    }
  });
  return statusMapping;
};
// export const getFiltersByNames = (filterNames: Filter[]) => createSelector(selectAllFilters,
//     (filters) => {
//         return filters.filter(i => filterNames.findIndex(j => j === i.Name) !== -1);
//     });
export const FilterReducer = slice.reducer;
